import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import ForcePasswordChange from "@/pages/ForcePasswordChange";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import SetPassword from "@/pages/SetPassword";
import Dashboard from "@/pages/Dashboard";
import Recruitment from "@/pages/Recruitment";
import CandidatePipeline from "@/pages/CandidatePipeline";
import ApplyForm from "@/pages/ApplyForm";
import CandidatePortal from "@/pages/CandidatePortal";
import OfferSign from "@/pages/OfferSign";
import Employees from "@/pages/Employees";
import EmployeeDetail from "@/pages/EmployeeDetail";
import Requests from "@/pages/Requests";
import Approvals from "@/pages/Approvals";
import BankHolidays from "@/pages/BankHolidays";
import Settings from "@/pages/Settings";
import TeamCalendar from "@/pages/TeamCalendar";
import ReportHub from "@/pages/ReportHub";
import MyTotalReward from "@/pages/MyTotalReward";
import ReportScreen from "@/pages/ReportScreen";
import AuditTrail from "@/pages/AuditTrail";
import Users from "@/pages/Users";
import Notifications from "@/pages/Notifications";
import HRHub from "@/pages/HRHub";
import MyHR from "@/pages/MyHR";
import Learning from "@/pages/Learning";
import Wellbeing from "@/pages/Wellbeing";
import Help from "@/pages/Help";
import Directory from "@/pages/Directory";
import PreEmployment from "@/pages/PreEmployment";
import Performance from "@/pages/people/Performance";
import Training from "@/pages/people/Training";
import Cases from "@/pages/people/Cases";
import DocumentsPolicies from "@/pages/people/DocumentsPolicies";
import EmailSignatures from "@/pages/EmailSignatures";
import TrainingSign from "@/pages/TrainingSign";
import { ComingSoon, ModulePreviewBanner } from "@/components/ComingSoon";

function Protected({ children, roles, module }) {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen grid place-items-center text-slate-400">Loading…</div>;
  if (user === false) return <Navigate to={`/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`} replace />;
  if (user.must_change_password) return <ForcePasswordChange />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  const moduleOff = module && user.modules && user.modules[module] === false;
  if (moduleOff && user.role !== "admin") return <Layout><ComingSoon module={module} /></Layout>;
  if (moduleOff && user.role === "admin") return <Layout><ModulePreviewBanner module={module} />{children}</Layout>;
  return <Layout>{children}</Layout>;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/set-password" element={<SetPassword />} />
      <Route path="/apply/:vid" element={<ApplyForm />} />
      <Route path="/portal/:token" element={<CandidatePortal />} />
      <Route path="/offer/:token" element={<OfferSign />} />
      <Route path="/contract/:token" element={<OfferSign kind="contract" />} />
      <Route path="/onboarding/:token" element={<PreEmployment />} />
      <Route path="/" element={<Protected><Dashboard /></Protected>} />
      <Route path="/employees" element={<Protected roles={["admin", "hr_admin", "manager"]}><Employees /></Protected>} />
      <Route path="/recruitment" element={<Protected roles={["admin", "hr_admin", "manager"]} module="recruitment"><Recruitment /></Protected>} />
      <Route path="/recruitment/:vid" element={<Protected roles={["admin", "hr_admin", "manager"]} module="recruitment"><CandidatePipeline /></Protected>} />
      <Route path="/employees/:id" element={<Protected><EmployeeDetail /></Protected>} />
      <Route path="/requests" element={<Protected><Requests /></Protected>} />
      <Route path="/approvals" element={<Protected roles={["admin", "hr_admin", "manager"]}><Approvals /></Protected>} />
      <Route path="/calendar" element={<Protected><TeamCalendar /></Protected>} />
      <Route path="/bank-holidays" element={<Protected roles={["admin"]}><BankHolidays /></Protected>} />
      <Route path="/settings" element={<Protected roles={["admin"]}><Settings /></Protected>} />
      <Route path="/hr-hub" element={<Protected roles={["admin", "hr_admin"]}><HRHub /></Protected>} />
      <Route path="/people/performance" element={<Protected roles={["admin", "hr_admin", "manager"]}><Performance /></Protected>} />
      <Route path="/people/training" element={<Protected roles={["admin", "hr_admin", "manager"]} module="learning"><Training /></Protected>} />
      <Route path="/people/cases" element={<Protected roles={["admin", "hr_admin", "manager"]}><Cases /></Protected>} />
      <Route path="/people/documents" element={<Protected roles={["admin", "hr_admin"]}><DocumentsPolicies /></Protected>} />
      <Route path="/email-signatures" element={<Protected roles={["admin", "hr_admin"]}><EmailSignatures /></Protected>} />
      <Route path="/my-hr" element={<Protected><MyHR /></Protected>} />
      <Route path="/training/:tid/sign" element={<Protected><TrainingSign /></Protected>} />
      <Route path="/learning" element={<Protected module="learning"><Learning /></Protected>} />
      <Route path="/wellbeing" element={<Protected module="wellbeing"><Wellbeing /></Protected>} />
      <Route path="/help" element={<Protected module="help"><Help /></Protected>} />
      <Route path="/directory" element={<Protected module="directory"><Directory /></Protected>} />
      <Route path="/reports" element={<Protected roles={["admin", "hr_admin", "manager", "read_only"]} module="reports"><ReportHub /></Protected>} />
      <Route path="/reports/:key" element={<Protected roles={["admin", "hr_admin", "manager", "read_only"]} module="reports"><ReportScreen /></Protected>} />
      <Route path="/my-total-reward" element={<Protected module="benefits"><MyTotalReward /></Protected>} />
      <Route path="/audit" element={<Protected roles={["admin"]}><AuditTrail /></Protected>} />
      <Route path="/users" element={<Protected roles={["admin"]}><Users /></Protected>} />
      <Route path="/notifications" element={<Protected><Notifications /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}
